'use client';
import { ReactElement, useState } from 'react';
import { Avatar, Box, Skeleton, Tooltip, Typography } from '@mui/material';

import { useT } from '@/hooks';
import {
  usePublicProfile,
  useTraderStats,
  useUserValue,
} from '@/services/query/leaderboard.queries';
import { LeaderboardTrader } from '@/types';
import { formatCompactNumber, getAvatarGradient } from '@/utils';
import { formatDate } from '@/utils/formatters';

interface TraderHoverCardProps {
  trader: LeaderboardTrader;
  children: ReactElement;
  index?: number;
  orderBy?: 'PNL' | 'VOL';
}

const formatStat = (value: number, isCurrency: boolean = false) => {
  const absValue = Math.abs(value);
  const formatted = formatCompactNumber(absValue);
  const sign = value < 0 ? '-' : '';
  const prefix = isCurrency ? '$' : '';
  return `${sign}${prefix}${formatted}`;
};

export const TraderHoverCard = ({ trader, children, index = 0, orderBy }: TraderHoverCardProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const { t } = useT();

  //for getting the live stats
  const { data: liveStats, isFetching } = useTraderStats(
    {
      user: trader?.address || trader?.id || trader?.proxyWallet || '',
      category: 'OVERALL',
      timePeriod: 'ALL',
      orderBy: orderBy || 'PNL',
      offset: 0,
    },
    { enabled: isHovered }
  );

  const activeTrader = liveStats || trader;
  const showSkeleton = isFetching;

  //for getting the positions value
  const { data: userValue, isFetching: isFetchingValue } = useUserValue(
    trader?.address || trader?.id || trader?.proxyWallet || '',
    { enabled: isHovered }
  );
  //for getting the joined date
  const { data: publicProfile, isFetching: isFetchingProfile } = usePublicProfile(
    trader?.address || trader?.id || trader?.proxyWallet || '',
    { enabled: isHovered }
  );

  const showSkeletonValue = isFetchingValue || userValue === undefined;
  const positionValueDisplay = formatStat(userValue ?? 0, true);

  const joinedDate = publicProfile?.createdAt
    ? `${t('leaderboardPortfolio.joined')} ${formatDate(publicProfile.createdAt, { month: 'short', year: 'numeric' })}`
    : '';

  const pnlColor =
    activeTrader?.pnl > 0 ? '#22c55e' : activeTrader?.pnl < 0 ? '#ef4444' : 'text.primary';

  const avatarBackground =
    activeTrader?.profileImage ||
    getAvatarGradient(activeTrader?.address || '', activeTrader?.id, index);
  const displayName = activeTrader?.userName || activeTrader?.address || 'Unknown Trader';
  const pnlDisplay = formatStat(activeTrader?.pnl || 0, true);
  const volumeDisplay = formatStat(activeTrader?.volume || 0, true);

  const tooltipContent = (
    <Box sx={{ minWidth: 260 }}>
      {/* Header section */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Avatar
          sx={{
            width: 52,
            height: 52,
            background: avatarBackground,
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              fontSize: '1.0625rem',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: 1.2,
            }}
          >
            {displayName}
          </Typography>
          {isFetchingProfile ? (
            <Skeleton variant="text" width={100} height={20} sx={{ mt: 0.5 }} />
          ) : joinedDate ? (
            <Typography
              sx={{ color: 'text.secondary', fontSize: '0.875rem', mt: 0.5, fontWeight: 500 }}
            >
              {joinedDate}
            </Typography>
          ) : null}
        </Box>
      </Box>

      {/* Stats row */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          p: 2,
          pt: 2,
        }}
      >
        {/* Positions */}
        <Box sx={{ flex: 1 }}>
          {showSkeletonValue ? (
            <Skeleton variant="text" width={60} height={24} sx={{ my: '2px' }} />
          ) : (
            <Typography sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.9375rem' }}>
              {positionValueDisplay}
            </Typography>
          )}
          <Typography sx={{ color: 'text.secondary', fontSize: '0.75rem', mt: 0.5 }}>
            {t('leaderboardPortfolio.positions')}
          </Typography>
        </Box>

        {/* Profit/Loss */}
        <Box sx={{ flex: 1 }}>
          {showSkeleton ? (
            <Skeleton variant="text" width={60} height={24} sx={{ my: '2px' }} />
          ) : (
            <Typography sx={{ fontWeight: 700, color: pnlColor, fontSize: '0.9375rem' }}>
              {pnlDisplay}
            </Typography>
          )}
          <Typography sx={{ color: 'text.secondary', fontSize: '0.75rem', mt: 0.5 }}>
            {t('leaderboard.profit')}
          </Typography>
        </Box>

        {/* Volume */}
        <Box sx={{ flex: 1, textAlign: 'right' }}>
          {showSkeleton ? (
            <Skeleton variant="text" width={60} height={24} sx={{ my: '2px', ml: 'auto' }} />
          ) : (
            <Typography sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.9375rem' }}>
              {volumeDisplay}
            </Typography>
          )}
          <Typography sx={{ color: 'text.secondary', fontSize: '0.75rem', mt: 0.5 }}>
            {t('leaderboard.volume')}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Tooltip
      title={tooltipContent}
      placement="bottom-start"
      arrow={false}
      onOpen={() => setIsHovered(true)}
      onClose={() => setIsHovered(false)}
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: 'background.paper',
            color: 'text.primary',
            boxShadow: '0px 8px 30px rgba(0, 0, 0, 0.08)',
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            p: 0,
            overflow: 'hidden',
          },
        },
      }}
    >
      <Box component="span" sx={{ cursor: 'pointer', display: 'inline-flex' }}>
        {children}
      </Box>
    </Tooltip>
  );
};
